import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Users, Coins, ClipboardList, Flag, CheckCircle2, XCircle,
  ShieldCheck, ExternalLink, Crown,
} from "lucide-react";
import { api, formatGBP, formatErr } from "../lib/api";
import TrustBadge from "../components/TrustBadge";
import { logger } from "../lib/logger";

const TABS = [
  { id: "pending", label: "Pending review" },
  { id: "approved", label: "Approved" },
  { id: "rejected", label: "Rejected" },
  { id: "users", label: "Users" },
  { id: "reports", label: "Reports" },
];

export default function AdminDashboard() {
  const [stats, setStats] = useState({});
  const [tab, setTab] = useState("pending");
  const [campaigns, setCampaigns] = useState([]);
  const [users, setUsers] = useState([]);
  const [reports, setReports] = useState([]);
  const [busy, setBusy] = useState(false);

  const loadStats = async () => {
    try {
      const { data } = await api.get("/admin/stats");
      setStats(data);
    } catch (err) {
      logger.warn("Admin stats load failed", err);
    }
  };

  const loadTab = async (t) => {
    try {
      if (t === "users") {
        const { data } = await api.get("/admin/users");
        setUsers(data.items || []);
      } else if (t === "reports") {
        const { data } = await api.get("/admin/reports");
        setReports(data.items || []);
      } else {
        const { data } = await api.get("/admin/campaigns", { params: { status: t } });
        setCampaigns(data.items || []);
      }
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  useEffect(() => { loadStats(); }, []);
  useEffect(() => { loadTab(tab); /* eslint-disable-next-line */ }, [tab]);

  const approve = async (id) => {
    setBusy(true);
    try {
      await api.post(`/admin/campaigns/${id}/approve`);
      toast.success("Campaign approved and now live.");
      await Promise.all([loadTab(tab), loadStats()]);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  const reject = async (id) => {
    setBusy(true);
    try {
      await api.post(`/admin/campaigns/${id}/reject`);
      toast.success("Campaign rejected.");
      await Promise.all([loadTab(tab), loadStats()]);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  const verifyUser = async (userId, level) => {
    try {
      await api.post(`/admin/users/${userId}/verify`, null, { params: { level } });
      toast.success(`User verification set to ${level.replace("_", " ")}`);
      await loadTab("users");
    } catch (e) { toast.error(formatErr(e)); }
  };

  const resolveReport = async (id) => {
    try {
      await api.post(`/admin/reports/${id}/resolve`);
      toast.success("Report resolved");
      await Promise.all([loadTab("reports"), loadStats()]);
    } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-12 sm:py-16">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <span className="eyebrow inline-flex items-center gap-2"><Crown size={12} strokeWidth={1.8} /> Admin control</span>
        <h1 className="font-serif-display text-4xl sm:text-5xl mt-3 leading-tight">Steward of trust.</h1>
        <p className="text-brand-muted mt-3 max-w-xl">Review campaigns, vouch for users, and protect the integrity of every story shared.</p>
      </motion.div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mt-10">
        <StatCard label="Pending" value={stats.pending_campaigns} Icon={ClipboardList} />
        <StatCard label="Approved" value={stats.approved_campaigns} Icon={CheckCircle2} />
        <StatCard label="Total raised" value={formatGBP(stats.total_raised || 0)} Icon={Coins} highlight />
        <StatCard label="Donations" value={stats.total_donations} Icon={ShieldCheck} />
        <StatCard label="Open reports" value={stats.open_reports} Icon={Flag} />
      </div>

      <div className="mt-12 border-b border-brand">
        <div className="flex gap-7 overflow-x-auto">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`pb-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors
                ${tab === t.id ? "border-brand-primary text-brand-primary" : "border-transparent text-brand-muted hover:text-brand-text"}`}
              data-testid={`admin-tab-${t.id}`}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-8">
        <AdminTabContent
          tab={tab}
          users={users}
          reports={reports}
          campaigns={campaigns}
          onVerify={verifyUser}
          onResolve={resolveReport}
          onApprove={approve}
          onReject={reject}
          busy={busy}
        />
      </div>
    </div>
  );
}

function AdminTabContent({ tab, users, reports, campaigns, onVerify, onResolve, onApprove, onReject, busy }) {
  if (tab === "users") return <UserTable users={users} onVerify={onVerify} />;
  if (tab === "reports") return <ReportList reports={reports} onResolve={onResolve} />;
  return <CampaignList items={campaigns} status={tab} onApprove={onApprove} onReject={onReject} busy={busy} />;
}

function StatCard({ label, value, Icon, highlight }) {
  return (
    <div className={`premium-card p-5 ${highlight ? "bg-brand-primary-light/60" : ""}`}>
      <Icon size={18} strokeWidth={1.6} className="text-brand-primary mb-2" />
      <p className="font-serif-display text-2xl">{value ?? 0}</p>
      <p className="eyebrow mt-1">{label}</p>
    </div>
  );
}

function CampaignList({ items, status, onApprove, onReject, busy }) {
  if (items.length === 0) {
    return <p className="text-brand-muted">No {status} campaigns.</p>;
  }
  return (
    <div className="space-y-4">
      {items.map((c) => (
        <div key={c.id} className="premium-card p-6" data-testid={`admin-campaign-${c.id}`}>
          <div className="flex flex-col lg:flex-row gap-5">
            {c.cover_image_url && (
              <img src={c.cover_image_url} alt="" className="w-full lg:w-44 h-32 rounded-2xl object-cover" />
            )}
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <span className="eyebrow">{c.category}</span>
                <TrustBadge level={c.creator_verification} />
                <span className="text-xs text-brand-muted">by {c.creator_name}</span>
              </div>
              <h3 className="font-serif-display text-2xl text-brand-text">{c.title}</h3>
              <p className="text-sm text-brand-muted mt-2 line-clamp-2">{c.story}</p>
              <div className="flex flex-wrap items-center gap-4 mt-3 text-xs text-brand-muted">
                <span>Goal: <span className="text-brand-text font-medium">{formatGBP(c.amount_needed)}</span></span>
                <span>Trust: <span className="text-brand-primary font-medium">{c.trust_score}/100</span></span>
                <span>Urgency: <span className="text-brand-text font-medium">{c.urgency}</span></span>
                {c.proof_urls?.length > 0 && <span>{c.proof_urls.length} proof file(s)</span>}
              </div>
            </div>
            <div className="flex lg:flex-col gap-2 items-stretch">
              <Link to={`/campaigns/${c.id}`} className="btn-outline-gold !py-2 !px-4 text-sm inline-flex items-center gap-1.5">
                <ExternalLink size={13} strokeWidth={1.8} /> View
              </Link>
              {status === "pending" && (
                <>
                  <button onClick={() => onApprove(c.id)} disabled={busy} className="btn-gold !py-2 !px-4 text-sm" data-testid={`approve-${c.id}`}>
                    <CheckCircle2 size={14} strokeWidth={1.8} className="mr-1.5" /> Approve
                  </button>
                  <button onClick={() => onReject(c.id)} disabled={busy} className="btn-pink !py-2 !px-4 text-sm" data-testid={`reject-${c.id}`}>
                    <XCircle size={14} strokeWidth={1.8} className="mr-1.5" /> Reject
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function UserTable({ users, onVerify }) {
  if (users.length === 0) return <p className="text-brand-muted">No users yet.</p>;
  const LEVELS = ["unverified", "community_verified", "platform_verified", "high_trust"];
  return (
    <div className="premium-card overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-brand-bg border-b border-brand">
          <tr>
            <th className="text-left px-5 py-3 text-brand-muted font-medium">User</th>
            <th className="text-left px-5 py-3 text-brand-muted font-medium hidden md:table-cell">Role</th>
            <th className="text-left px-5 py-3 text-brand-muted font-medium">Verification</th>
            <th className="text-right px-5 py-3 text-brand-muted font-medium">Action</th>
          </tr>
        </thead>
        <tbody>
          {users.map((u) => (
            <tr key={u.id} className="border-b border-brand last:border-0 hover:bg-brand-bg/50" data-testid={`admin-user-${u.id}`}>
              <td className="px-5 py-4">
                <p className="font-medium text-brand-text">{u.name}</p>
                <p className="text-xs text-brand-muted">{u.email}</p>
              </td>
              <td className="px-5 py-4 hidden md:table-cell capitalize">{u.role}</td>
              <td className="px-5 py-4"><TrustBadge level={u.verification_level || "unverified"} /></td>
              <td className="px-5 py-4 text-right">
                <select onChange={(e) => onVerify(u.id, e.target.value)} value=""
                        className="input-premium !py-1.5 !px-2 text-xs inline-block w-auto" data-testid={`verify-select-${u.id}`}>
                  <option value="" disabled>Set level…</option>
                  {LEVELS.map((l) => <option key={l} value={l}>{l.replace("_", " ")}</option>)}
                </select>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ReportList({ reports, onResolve }) {
  if (reports.length === 0) return <p className="text-brand-muted">No reports yet.</p>;
  return (
    <div className="space-y-3">
      {reports.map((r) => (
        <div key={r.id} className={`premium-card p-5 ${r.resolved ? "opacity-60" : ""}`}>
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <p className="eyebrow inline-flex items-center gap-2 mb-1"><Flag size={11} strokeWidth={1.8} /> Report</p>
              <p className="text-brand-text">{r.reason}</p>
              <Link to={`/campaigns/${r.campaign_id}`} className="text-brand-primary text-xs hover:underline mt-1 inline-block">
                View campaign →
              </Link>
            </div>
            {!r.resolved && (
              <button onClick={() => onResolve(r.id)} className="btn-gold !py-1.5 !px-3 text-xs" data-testid={`resolve-${r.id}`}>
                Resolve
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
